import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '../api/client';

interface User {
    id: string;
    email: string;
    nomeCompleto: string;
    grupoAsaas: string;
    role?: string;
    permissions?: string[];
    representanteId?: number | null;
}

interface AuthContextData {
    user: User | null;
    isAuthenticated: boolean;
    loading: boolean;
    login: (token: string, userData: User) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadUser = async () => {
            const token = localStorage.getItem('nexo_token');
            const storedUser = localStorage.getItem('nexo_user');

            if (token) {
                if (storedUser) {
                    setUser(JSON.parse(storedUser));
                    setLoading(false); // libera render imediatamente com dados do cache
                }

                // valida token e atualiza dados em background sem bloquear a UI
                apiClient.get('/usuarios/perfil').then((response) => {
                    const freshUser = response.data;
                    setUser(freshUser);
                    localStorage.setItem('nexo_user', JSON.stringify(freshUser));
                }).catch((error) => {
                    console.error('Erro ao validar sessão/carregar perfil:', error);
                }).finally(() => {
                    if (!storedUser) setLoading(false);
                });

                return; // loading já foi definido acima se havia storedUser
            }
            setLoading(false);
        };

        loadUser();
    }, []);

    const login = (token: string, userData: User) => {
        localStorage.setItem('nexo_token', token);
        localStorage.setItem('nexo_user', JSON.stringify(userData));
        setUser(userData);
    };

    const logout = () => {
        localStorage.removeItem('nexo_token');
        localStorage.removeItem('nexo_user');
        setUser(null);
        window.location.href = '/login';
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
